var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import { OperationRead } from '@galigeo-store/galigeojs';
import './BasicStatus.js';
const OP_TYPE_ICON_NAME = {
    add: 'plus',
    modify: 'edit',
    delete: 'delete',
    transfer: 'swap',
};
/** Color shared with `<operation-card>` so the same operation type
 *  renders consistently across both surfaces. Transfer uses a custom hex
 *  since no named variant maps onto its purple shade. */
const OP_TYPE_COLOR = {
    add: 'success',
    modify: 'warning',
    transfer: '#6e44a3',
    delete: 'danger',
};
function opTypeLabel(type, count) {
    const plural = count > 1;
    switch (type) {
        case 'add':
            return plural
                ? msg('Additions', { id: 'scenarioCard.opType.add.plural' })
                : msg('Addition', { id: 'scenarioCard.opType.add.singular' });
        case 'modify':
            return plural
                ? msg('Modifications', { id: 'scenarioCard.opType.modify.plural' })
                : msg('Modification', { id: 'scenarioCard.opType.modify.singular' });
        case 'transfer':
            return plural
                ? msg('Transfers', { id: 'scenarioCard.opType.transfer.plural' })
                : msg('Transfer', { id: 'scenarioCard.opType.transfer.singular' });
        case 'delete':
            return plural
                ? msg('Removals', { id: 'scenarioCard.opType.delete.plural' })
                : msg('Removal', { id: 'scenarioCard.opType.delete.singular' });
    }
}
/**
 * Sidebar card for a Study — renders as a full-width row inside a
 * scrolling list. Status pill (clickable for inline picker), description
 * (2-line clamp), op-count badges by type, a delete action revealed on
 * hover, and a meta line. The action row collapses for `validated`
 * studies (locked).
 */
let StudyCard = class StudyCard extends LitElement {
    constructor() {
        super(...arguments);
        this.changes = [];
        this.selected = false;
        this.pinHovered = false;
    }
    _emitClick(e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('card-click', {
            detail: this.study,
            bubbles: true,
            composed: true
        }));
    }
    _emitDelete(e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('delete-study', {
            detail: this.study,
            bubbles: true,
            composed: true
        }));
    }
    _emitZoomTo(e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('zoom-to-study', {
            detail: this.study,
            bubbles: true,
            composed: true
        }));
    }
    _emitShare(e) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('share-study', {
            detail: this.study,
            bubbles: true,
            composed: true
        }));
    }
    /** The pill click is the entry point for the inline status picker.
     *  We forward the click target so the consumer can position a popover
     *  next to it. */
    _emitStatusClick(e) {
        e.stopPropagation();
        const target = e.currentTarget;
        this.dispatchEvent(new CustomEvent('status-pill-click', {
            detail: { study: this.study, anchor: target },
            bubbles: true,
            composed: true
        }));
    }
    /** Status pill base colors — aligned on the RZI mockup's blue-navy
     *  family (same hexes as the list filter-status icons). `basic-status`
     *  derives the pale background / dark text / dot from this base. The
     *  `canceled` status isn't part of the mockup; it leans toward red to
     *  signal "stopped/abandoned" but stays desaturated to match the muted
     *  chroma of the blue scale rather than a loud alert red. */
    _statusColor(status) {
        switch (status) {
            case 'validated':
                return '#122347';
            case 'in_progress':
                return '#6f86c4';
            case 'canceled':
                return '#b56b6b';
            case 'draft':
            default:
                return '#9aa6c8';
        }
    }
    _statusLabel(status) {
        switch (status) {
            case 'validated':
                return msg('Validated', {
                    id: 'scenario.status.validated',
                    desc: 'Study status: review-locked'
                });
            case 'in_progress':
                return msg('In progress', {
                    id: 'scenario.status.inProgress',
                    desc: 'Study status: actively being edited'
                });
            case 'canceled':
                return msg('Cancelled', {
                    id: 'scenario.status.cancelled',
                    desc: 'Study status: abandoned'
                });
            case 'draft':
            default:
                return msg('Draft', {
                    id: 'scenario.status.draft',
                    desc: 'Study status: initial editable state'
                });
        }
    }
    _renderOpBadge(type, count) {
        return html `
      <basic-status
        class="op-badge"
        .variant=${OP_TYPE_COLOR[type]}
        .icon=${OP_TYPE_ICON_NAME[type]}
        .status=${`${count} ${opTypeLabel(type, count)}`}
        size="small"
      ></basic-status>
    `;
    }
    _countsByType() {
        const counts = {
            add: 0,
            modify: 0,
            transfer: 0,
            delete: 0
        };
        for (const c of this.changes) {
            switch (c.type) {
                case OperationRead.type.ADD:
                    counts.add++;
                    break;
                case OperationRead.type.EDIT:
                    counts.modify++;
                    break;
                case OperationRead.type.TRANSFER:
                    counts.transfer++;
                    break;
                case OperationRead.type.DELETE:
                    counts.delete++;
                    break;
            }
        }
        return counts;
    }
    _renderOps() {
        const c = this._countsByType();
        const total = c.add + c.modify + c.transfer + c.delete;
        if (total === 0) {
            return html `<span class="row-ops-empty"
        >${msg('No operation', {
                id: 'scenarioCard.ops.empty',
                desc: 'Hint shown when a study has zero changes'
            })}</span
      >`;
        }
        const items = [];
        if (c.add > 0)
            items.push(this._renderOpBadge('add', c.add));
        if (c.modify > 0)
            items.push(this._renderOpBadge('modify', c.modify));
        if (c.transfer > 0)
            items.push(this._renderOpBadge('transfer', c.transfer));
        if (c.delete > 0)
            items.push(this._renderOpBadge('delete', c.delete));
        return html `${items}`;
    }
    render() {
        const s = this.study;
        const isLocked = s.status === 'validated';
        return html `
      <div
        class="row ${isLocked ? 'is-locked' : ''}"
        role="button"
        tabindex="0"
        @click=${this._emitClick}
        title=${s.description ?? ''}
      >
        <span class="row-name">${s.name}</span>

        <button
          type="button"
          class="row-status-btn"
          @click=${this._emitStatusClick}
        >
          <basic-status
            .variant=${this._statusColor(s.status)}
            .status=${this._statusLabel(s.status)}
            size="small"
            dot
          >
            ${isLocked ? html `<basic-icon name="lock" size="9"></basic-icon>` : ''}
          </basic-status>
        </button>

        ${s.description
            ? html `<div class="row-desc">${s.description}</div>`
            : html `<div class="row-desc"></div>`}

        <div class="row-impact">
          <div class="row-ops">${this._renderOps()}</div>
        </div>

        <div class="row-meta">
          <span class="row-meta-text">${this.metaText ?? ''}</span>
          <div class="row-actions">
            <button
              type="button"
              class="row-action-btn"
              title=${msg('Center on map', {
            id: 'scenarioCard.action.zoomTo.title',
            desc: 'Tooltip on the center-study-on-map button'
        })}
              aria-label=${msg('Center this study on the map', {
            id: 'scenarioCard.action.zoomTo.aria',
            desc: 'Accessible label for the center-on-map button'
        })}
              @click=${this._emitZoomTo}
            >
              <basic-icon name="target" size="14"></basic-icon>
            </button>
            <button
              type="button"
              class="row-action-btn"
              disabled
              title=${msg('Sharing is not available yet', {
            id: 'scenarioCard.action.share.disabledTitle',
            desc: 'Tooltip on the disabled share button of a study card'
        })}
              aria-label=${msg('Share this study', {
            id: 'scenarioCard.action.share.aria',
            desc: 'Accessible label for the study share button'
        })}
              @click=${this._emitShare}
            >
              <basic-icon name="share" size="14"></basic-icon>
            </button>
            ${!isLocked
            ? html `
                  <button
                    type="button"
                    class="row-action-btn danger"
                    title=${msg('Delete', {
                id: 'scenarioCard.action.delete.title',
                desc: 'Tooltip on the delete button of a study card'
            })}
                    aria-label=${msg('Delete study', {
                id: 'scenarioCard.action.delete.aria',
                desc: 'Accessible label for the study delete button'
            })}
                    @click=${this._emitDelete}
                  >
                    <basic-icon name="delete" size="14"></basic-icon>
                  </button>
                `
            : ''}
          </div>
        </div>
      </div>
    `;
    }
};
StudyCard.styles = css `
    :host {
      display: block;
      font-family: inherit;
    }

    .row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      grid-template-rows: auto auto auto auto;
      column-gap: 8px;
      padding: 12px 16px;
      cursor: pointer;
      border-left: 3px solid transparent;
      transition: background 0.15s, border-color 0.15s;
      position: relative;
    }
    .row:hover {
      background: #f8fbff;
    }
    :host([selected]) .row {
      background: #e6f2ff;
      border-left-color: #0078d4;
    }
    :host([pin-hovered]:not([selected])) .row {
      background: #eef6ff;
      border-left-color: #0078d4;
      box-shadow: inset 0 0 0 1px rgba(0, 120, 212, 0.18);
    }

    .row-name {
      grid-column: 1;
      grid-row: 1;
      font-size: 14px;
      font-weight: 600;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      align-self: center;
      min-width: 0;
    }

    /* Status pill — a clickable wrapper around <basic-status>. The pill
       chrome (background, border, dot) lives inside <basic-status>; the
       wrapper button only owns the grid placement + click affordance. */
    .row-status-btn {
      grid-column: 2;
      grid-row: 1;
      align-self: center;
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font: inherit;
      flex-shrink: 0;
      transition: filter 0.15s;
    }
    .row-status-btn:hover {
      filter: brightness(0.96);
    }

    .row-desc {
      grid-column: 1 / span 2;
      grid-row: 2;
      font-size: 12.5px;
      color: #6a7585;
      margin-top: 2px;
      margin-bottom: 6px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    /* L3 — ops on the left, action buttons on the right */
    .row-impact {
      grid-column: 1 / span 2;
      grid-row: 3;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 6px;
      min-height: 26px;
    }
    .row-ops {
      display: flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
      flex: 1;
      min-width: 0;
    }
    .row-ops-empty {
      font-size: 11px;
      color: #a8b3bf;
      font-style: italic;
    }
    /* Op-count badges are <basic-status> instances; pill chrome lives
       inside that component. */
    .op-badge {
      flex-shrink: 0;
    }

    .row-actions {
      display: flex;
      flex-direction: row;
      gap: 4px;
      align-items: center;
      flex-shrink: 0;
      opacity: 0;
      transition: opacity 0.15s;
    }
    .row:hover .row-actions {
      opacity: 1;
    }
    .row-action-btn {
      width: 26px;
      height: 26px;
      background: #fff;
      border: 1px solid #d8e0e8;
      border-radius: 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: #6a7585;
      padding: 0;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }
    .row-action-btn:hover {
      background: #f0f5fa;
      border-color: #a0c8f0;
      color: #0078d4;
    }
    .row-action-btn.danger:hover {
      background: #fdeeec;
      border-color: #e8736b;
      color: #c0392b;
    }
    /* Disabled action (e.g. share, pending the email composer) — dimmed and
       non-interactive; the hover affordances are neutralized. */
    .row-action-btn:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .row-action-btn:disabled:hover {
      background: #fff;
      border-color: #d8e0e8;
      color: #6a7585;
    }

    /* Meta line (L4) — "Modifié il y a X · auteur" on the left, the action
       buttons (center-on-map / share / delete) on the right, mirroring the
       mockup's .study-item-meta-row. min-height reserves the action-button
       height so the hover reveal doesn't shift the layout. Center-on-map
       and share stay available on every status; only delete is gated by
       the locked (validated) state via conditional rendering. */
    .row-meta {
      grid-column: 1 / span 2;
      grid-row: 4;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
      min-height: 26px;
      font-size: 11.5px;
      color: #8a95a5;
    }
    .row-meta-text {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  `;
__decorate([
    property({ type: Object })
], StudyCard.prototype, "study", void 0);
__decorate([
    property({ type: Array })
], StudyCard.prototype, "changes", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], StudyCard.prototype, "selected", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'pin-hovered' })
], StudyCard.prototype, "pinHovered", void 0);
__decorate([
    property({ type: String, attribute: 'meta-text' })
], StudyCard.prototype, "metaText", void 0);
StudyCard = __decorate([
    localized(),
    customElement('study-card')
], StudyCard);
export { StudyCard };
//# sourceMappingURL=StudyCard.js.map